import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthTokens, PermAction, PermissionMap, SessionUser } from '@/api/contracts';
import {
  api,
  ApiError,
  clearReferenceCache,
  companyStore,
  onDataChanged,
  onPasswordChangeRequired,
  onSessionExpired,
  tokenStore,
} from '@/api/client';
import i18n, { loadRemoteBundle } from '@/i18n';

interface LoginResponse {
  tokens: AuthTokens;
  user: SessionUser;
}

interface MeResponse {
  user: SessionUser;
  permissions: PermissionMap;
  /** Set until the account picks its own password; every other route is refused meanwhile. */
  mustChangePassword?: boolean;
}

/** Shown once on the sign-in page after a forced change signs the user out. */
export const LOGIN_NOTICE_KEY = 'erb.login.notice';

interface AuthContextValue {
  user: SessionUser | null;
  permissions: PermissionMap;
  company: string | null;
  loading: boolean;
  /**
   * The session could not be checked — offline or a server error — but the
   * tokens were kept, since nothing said they were bad. Retry asks again.
   */
  sessionError: boolean;
  retrySession: () => void;
  /** The account must choose a new password before it may do anything else. */
  mustChangePassword: boolean;
  /**
   * Changes the password, then signs out everywhere — the server has already
   * ended every session — so the next sign-in proves the new password works.
   */
  changePassword: (current: string, next: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setCompany: (companyId: string) => void;
  /** Drives whether a button renders at all, mirroring the server's rules. */
  can: (doctype: string, action: PermAction) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** How often coming back to the tab re-reads the session, at most. */
const SESSION_RECHECK_MS = 30_000;

/** The same value, or the new one: keeps a re-read that changed nothing from re-rendering the app. */
const unlessEqual = <T,>(next: T) => (previous: T) =>
  JSON.stringify(previous) === JSON.stringify(next) ? previous : next;

/**
 * The session check. The API refuses any request whose X-Company the user may
 * not open — /auth/me included — so a stale remembered company would read as
 * a dead session; it is dropped and the check asked once more without it.
 */
async function fetchMe(): Promise<MeResponse> {
  try {
    return await api.get<MeResponse>('/auth/me');
  } catch (err) {
    if (err instanceof ApiError && err.status === 403 && companyStore.get()) {
      companyStore.set(null);
      return api.get<MeResponse>('/auth/me');
    }
    throw err;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [permissions, setPermissions] = useState<PermissionMap>({});
  const [company, setCompanyState] = useState<string | null>(companyStore.get());
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const queryClient = useQueryClient();

  const loadSession = useCallback(async () => {
    if (!tokenStore.access) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setSessionError(false);
    try {
      const me = await fetchMe();
      // A company remembered from an earlier session (or another person at
      // this browser) that this user may not open would send a header every
      // request is refused on. The same rule as the API's guard: an
      // administrator may open any company, anyone else only those listed.
      const stored = companyStore.get();
      const storedIsValid = stored !== null && (me.user.isAdmin || (me.user.companies ?? []).includes(stored));
      if (!storedIsValid) {
        const fallback = me.user.defaultCompany ?? null;
        companyStore.set(fallback);
        setCompanyState(fallback);
        // The wording loaded at start-up went out under the stale company
        // and was refused; ask again now that the header is right.
        void loadRemoteBundle(i18n.language);
      }
      // After the company is settled, so no screen asks under the stale one.
      setMustChangePassword(me.mustChangePassword === true);
      setUser(me.user);
      setPermissions(me.permissions);
    } catch (err) {
      // Only the server saying no ends the session. Offline, a timeout or a
      // 5xx used to sign everybody out of a branch on a bad connection.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        tokenStore.clear();
      } else {
        setSessionError(true);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  // The refresh token was refused mid-session: drop to the sign-in page
  // rather than leave every screen failing on tokens that cannot recover.
  useEffect(
    () =>
      onSessionExpired(() => {
        setUser(null);
        setPermissions({});
        clearReferenceCache();
        queryClient.clear();
      }),
    [queryClient],
  );

  /**
   * Re-reads who the user is and what they may do, without the loading state.
   *
   * Loaded once per page, the session went stale the moment anyone changed it:
   * a role granted or withdrawn, a company added, a permission ticked on the
   * Permissions screen — including by this very user — left the menu and every
   * button deciding on the old answer until a reload. It is asked again after
   * any write made here and whenever the tab comes back into view. The server
   * sends an access token issued before such a change back to refresh, so the
   * answer reflects the change rather than the old token's claims.
   */
  const signedIn = useRef(false);
  signedIn.current = user !== null;
  const lastCheck = useRef(0);
  const recheckSession = useCallback(async () => {
    if (!signedIn.current || !tokenStore.access) return;
    lastCheck.current = Date.now();
    try {
      const me = await api.get<MeResponse>('/auth/me');
      setMustChangePassword(me.mustChangePassword === true);
      setUser(unlessEqual<SessionUser | null>(me.user));
      setPermissions(unlessEqual(me.permissions));
    } catch (err) {
      // The company in use was withdrawn: the full check picks another.
      if (err instanceof ApiError && err.status === 403) void loadSession();
      // Anything else is the next screen's own request to report.
    }
  }, [loadSession]);

  useEffect(() => onDataChanged(() => void recheckSession()), [recheckSession]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheck.current < SESSION_RECHECK_MS) return;
      void recheckSession();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [recheckSession]);

  const retrySession = useCallback(() => void loadSession(), [loadSession]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api.post<LoginResponse>(
        '/auth/login',
        { email, password },
        { skipAuthRetry: true },
      );
      // Nothing cached belongs to the session starting here. Query keys name a
      // doctype and a filter but not a workspace, so without this the branches,
      // departments and designations behind every dropdown would still be the
      // previous customer's — served instantly, and only corrected on a
      // background refetch the reader has no reason to wait for.
      queryClient.clear();
      clearReferenceCache();
      tokenStore.set(result.tokens);
      companyStore.set(result.user.defaultCompany ?? null);
      setCompanyState(result.user.defaultCompany ?? null);
      setUser(result.user);
      // A sign-in may be into another workspace, whose wording differs.
      void loadRemoteBundle(i18n.language);
      const me = await api.get<MeResponse>('/auth/me');
      setMustChangePassword(me.mustChangePassword === true);
      setPermissions(me.permissions);
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.refresh;
    if (refreshToken) {
      await api.post('/auth/logout', { refreshToken }).catch(() => undefined);
    }
    tokenStore.clear();
    companyStore.set(null);
    setUser(null);
    setPermissions({});
    setCompanyState(null);
    setMustChangePassword(false);
    // Signing out has to empty the cache as well as the tokens: the next
    // person at this browser may be a different customer entirely.
    queryClient.clear();
    clearReferenceCache();
  }, [queryClient]);

  // HR reset the password mid-session: the next refused request says so.
  useEffect(() => onPasswordChangeRequired(() => setMustChangePassword(true)), []);

  const changePassword = useCallback(
    async (current: string, next: string) => {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      try {
        sessionStorage.setItem(LOGIN_NOTICE_KEY, 'password_changed');
      } catch {
        // Without storage the sign-in page simply shows no notice.
      }
      await logout();
    },
    [logout],
  );

  const setCompany = useCallback((companyId: string) => {
    companyStore.set(companyId);
    setCompanyState(companyId);
    // Everything cached is scoped to the previous legal entity.
    window.location.reload();
  }, []);

  const can = useCallback(
    (doctype: string, action: PermAction) =>
      user?.isAdmin === true || permissions[doctype]?.[action] === true,
    [permissions, user],
  );

  const value = useMemo(
    () => ({
      user,
      permissions,
      company,
      loading,
      sessionError,
      retrySession,
      mustChangePassword,
      changePassword,
      login,
      logout,
      setCompany,
      can,
    }),
    [
      user,
      permissions,
      company,
      loading,
      sessionError,
      retrySession,
      mustChangePassword,
      changePassword,
      login,
      logout,
      setCompany,
      can,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
